import PropTypes from 'prop-types';
import {useCallback, useEffect, useMemo} from 'react';
import useLabels from 'infrastructure/js/hooks/useLabels';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import Label from 'infrastructure/js/components/Label/label';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import Normalize from 'infrastructure/js/components/controls/controlsNormalizations';
import withPortal from 'infrastructure/js/components/HOCs/withPortal/withPortal';
import UnitHelper, {unitTypes} from 'infrastructure/js/utils/uomHelper';
import EntitiesMultiSelect from '../../../EntitiesMultiSelect/entitiesMultiSelect';

import './assetLengthListItem.scss';

const EntitiesMultiSelectPortal = withPortal(EntitiesMultiSelect);

const PORTAL_CLASS_NAMES = { menuPortal: () => 'assets-select-menu-portal' };


export default function AssetLengthListItem({ name,
                           index,
                           assetType,
                           change,
                           touch,
                           fetchConfig,
                           fieldData,
                           allFieldsData,
}) {

  const validationLabels = useLabels('mat.validation.messages.');

  const lengthLeft = fieldData?.asset?.assetsValue?.data?.lengthLeft ?? 0;
  const maxLength = UnitHelper.serverValueToUserValue(unitTypes.LENGTH, lengthLeft);


  const entitiesTypes = useMemo(() => [assetType], [assetType]);

  const cassetteIds = allFieldsData?.map(item => item?.asset?.assetsValue?.value) ?? [];
  const cassetteIdsKey = cassetteIds.join('|');
  const firstItemWidth = allFieldsData?.[0]?.asset?.assetsValue?.data?.widthInCentimeter;
  const firstItemMaterialId = allFieldsData?.[0]?.asset?.assetsValue?.data?.material?.id;

  const singleSelectionValidation = useCallback((value) => {
    const id = value?.value;
    const isDuplicated = cassetteIds.some((item, itenIndex) => {
      return item === id && itenIndex !== index;
    });

    if (isDuplicated) {
      return validationLabels.get('duplicatedSelection');
    }

    return undefined;
  }, [index, cassetteIdsKey]);

  const widthValidation = useCallback((value) => {
    const width = value?.data?.widthInCentimeter;

    if (width !== undefined && firstItemWidth !== undefined && width !== firstItemWidth) {
      return validationLabels.get('incorrectWidth');
    }

    return undefined;
  }, [firstItemWidth]);

  const materialValidation = useCallback((value) => {
    const materialId = value?.data?.material?.id;

    if (materialId !== undefined && firstItemMaterialId !== undefined && materialId !== firstItemMaterialId) {
      return validationLabels.get('incorrectMaterial');
    }

    return undefined;
  }, [firstItemMaterialId]);

  const validate = useMemo(() => [Validation.required, singleSelectionValidation, materialValidation, widthValidation,], [singleSelectionValidation, widthValidation, materialValidation]);

  const currentAssetValue = fieldData?.asset?.assetsValue;
  const currentId = currentAssetValue?.value;
  const currentWidth = currentAssetValue?.data?.widthInCentimeter;
  const currentMaterialId = currentAssetValue?.data?.material?.id;

  const isDuplicated = cassetteIds.some((item, itemIndex) => item === currentId && itemIndex !== index);
  const hasWidthMismatch = currentWidth !== undefined && firstItemWidth !== undefined && currentWidth !== firstItemWidth;
  const hasMaterialMismatch = currentMaterialId !== undefined && firstItemMaterialId !== undefined && currentMaterialId !== firstItemMaterialId;

  useEffect(() => {
    if (isDuplicated || hasWidthMismatch || hasMaterialMismatch) {
      touch(`${name}.[asset].assetsValue`);
    }
  }, [isDuplicated, hasWidthMismatch, hasMaterialMismatch, name, touch]);

  const onValueChangeCallback = useCallback((newValue, oldValue) => {
    // If cleared explicitly, reset length
    if (newValue === null) {
      change(`${name}.length`, null);
      return;
    }

    const newVal = newValue?.value;
    const oldVal = oldValue?.value;

    // No change - prevent clearing
    if (newVal === oldVal) {
      return;
    }

    const assetLength =  UnitHelper.serverValueToUserValue(unitTypes.LENGTH, newValue?.data?.lengthLeft);
    change(`${name}.length`, assetLength);

  }, [name, change]);

  const widthValueRenderer  = (value) => {
    const assetWidth = UnitHelper.serverValueToUserValueWithLabel(unitTypes.WIDTH, value?.data?.widthInCentimeter || 0);
    return (
      <Label className='column custom-data' text={assetWidth}  />);
  };

  return (
    <div className='asset-length-list-item'>
      <EntitiesMultiSelectPortal
        id={name + '.[asset]'}
        name={name + '.[asset]'}
        entitiesTypes={entitiesTypes}
        fetchConfig={fetchConfig}
        change={change}
        showEntitiesTypes={false}
        isMulti={false}
        validate={validate}
        onValueChangeCallback={onValueChangeCallback}
        classNames={PORTAL_CLASS_NAMES}
        customValueRenderer={widthValueRenderer}
        customOptionRenderer={widthValueRenderer}
      />
      <TextField id={name + '.[length]'}
                 name={name + '.[length]'}
                 className="asset-length"
                 normalize={Normalize.normalizeFloat(0, parseFloat(maxLength))}
                 validate={Validation.required}
                 placeholder={`${maxLength ? '0-' + parseFloat(maxLength) : 0} `}
      />
      </div>
  )
}

AssetLengthListItem.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  assetType: PropTypes.string.isRequired,
  touch: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  fetchConfig: PropTypes.object.isRequired,
  fieldData: PropTypes.object,
  allFieldsData: PropTypes.array.isRequired,
};
